import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { useUser } from "../../functional-components/UserContext";
import { transformHtml } from "../../utils/CmsUtils";
import { FaqArticle, FaqCategory } from "../../types/Faq";
import { faqUrl, useFaqData } from "../../hooks/FaqHooks";
import CockpitEditLink from "../CockpitEditLink";
import { useLang } from "../../hooks/LangHooks";
import { useMemo } from "react";
import { usePublicProducts } from "../../hooks/ProductHooks";
import { getProductByCockpitId, productUrl } from "../../utils/ProductUtils";
import ContentSection from "../ContentSection";
import { useProductTypes } from "../../hooks/SettingsHooks";
import find from "lodash/find";
import { productTypeBaseUrl } from "../../config/urls";
import "./style.scss";

const FaqItem = ({
  article,
  category,
  onTagClick = () => {}
}: {
  article: FaqArticle;
  category: FaqCategory;
  onTagClick?: () => void;
}) => {
  const t = useBjelinTranslations();
  const { admin } = useUser();
  const lang = useLang();
  const faqData = useFaqData(lang);
  const tags = useMemo(
    () => faqData.tags.filter(tag => article.tags.indexOf(tag._id) >= 0),
    [article, faqData]
  );
  const products = usePublicProducts();
  const productTypes = useProductTypes();

  return (
    <div className="faq-item-detail">
      <motion.h3>
        {admin && article.promoted_article && (
          <span
            className={"text-danger"}
            style={{ cursor: "help" }}
            title={"Promoted"}
          >
            *
          </span>
        )}
        {article.title}
      </motion.h3>
      <motion.div className="faq-item-description">
        {transformHtml(article.description)}
      </motion.div>
      {tags.length > 0 && (
        <div className="tag-list">
          {t("faq.tags")}:{" "}
          <ul>
            {tags.map(tag => (
              <li key={tag.id}>
                <Link to={faqUrl({ category, tag })} onClick={onTagClick}>
                  {tag.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {admin && (
        <ContentSection theme={"grey"} className={"mt-3"}>
          <CockpitEditLink
            collection={"faq_articles"}
            _id={article._id}
            labelKey={"action.edit"}
            style={{ color: "var(--bs-pink)" }}
          />
          <h4>FAQ Article Products</h4>
          <ul>
            {article.products.length === 0 && (
              <li>
                <em>None</em>
              </li>
            )}
            {article.products.map(_id => {
              const product = getProductByCockpitId(products, _id);
              const url = productUrl(product);
              return (
                <li key={_id}>
                  {product && url ? (
                    <Link to={url}>{product.name}</Link>
                  ) : (
                    <span>{_id}</span>
                  )}
                  <CockpitEditLink
                    _id={_id}
                    collection={"products"}
                    wrapper={children => <>{children}</>}
                    style={{ color: "var(--bs-pink)" }}
                  />
                </li>
              );
            })}
          </ul>
          <h4>FAQ Article Product Types</h4>
          <ul>
            {article.product_types.length === 0 && (
              <li>
                <em>None</em>
              </li>
            )}
            {article.product_types.map(_id => {
              const productType = find(productTypes, { _id });
              return (
                <li key={_id}>
                  {productType ? (
                    <Link to={productTypeBaseUrl + "/" + productType.path}>
                      {productType.name}
                    </Link>
                  ) : (
                    <span>{_id}</span>
                  )}
                  <CockpitEditLink
                    _id={_id}
                    collection={"product_types"}
                    labelKey={" "}
                    wrapper={children => <>{children}</>}
                    style={{ color: "var(--bs-pink)" }}
                  />
                </li>
              );
            })}
          </ul>
        </ContentSection>
      )}
    </div>
  );
};

export default FaqItem;
